const initOrlavenCollections = () => {
  document.querySelectorAll('[data-orlaven-collection]').forEach((section) => {
    if (section.dataset.orclInitialized === 'true') return;
    section.dataset.orclInitialized = 'true';

    const filterOpenButtons = Array.from(section.querySelectorAll('[data-orcl-open-filters]'));
    const filterClose = section.querySelector('[data-orcl-close-filters]');
    const filterOverlay = section.querySelector('[data-orcl-filter-overlay]');
    const filterSidebar = section.querySelector('.orcl__sidebar');
    const sortSelect = section.querySelector('[data-orcl-sort-select]');
    const sortForm = section.querySelector('[data-orcl-sort-form]');
    const filterForm = section.querySelector('.orcl__filters');
    const filterGroups = Array.from(section.querySelectorAll('.orcl__filter-group'));
    const countNode = section.querySelector('.orcl__count');
    const defaultCountText = countNode?.textContent || '';
    const cardGrid = section.querySelector('.orcl__grid');
    const cards = cardGrid ? Array.from(cardGrid.querySelectorAll('.orcl-card')) : [];
    const isLocalReviewPreview = ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname);
    const isSequenceCollection = cards.some((card) => card.hasAttribute('data-orcl-forced-order'));
    const isSequenceTagFallback = filterForm?.hasAttribute('data-orcl-sequence-tag-fallback');
    const loadMoreCache = new Map();
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let loadMoreObserver;
    let lastFilterTrigger;
    let filterCloseId = 0;

    const addLocalReviewPreview = (card) => {
      if (!isLocalReviewPreview) return;
      if (card.querySelector('.orcl-card__rating')) return;

      const handle = card.dataset.orclHandle || '';
      let hash = 2166136261;
      for (let index = 0; index < handle.length; index += 1) {
        hash = Math.imul(hash ^ handle.charCodeAt(index), 16777619);
      }

      const reviewCount = 1 + (hash % 87);
      const ratingValue = 4.6 + ((hash >>> 8) % 5) / 10;
      const title = card.querySelector('.orcl-card__title');
      const productUrl = title?.getAttribute('href') || '#';
      if (!title) return;

      const rating = document.createElement('a');
      rating.className = 'orcl-card__rating orcl-card__rating--demo';
      rating.href = `${productUrl}#reviews`;
      rating.setAttribute('aria-label', `${ratingValue.toFixed(1)} out of 5 from ${reviewCount} reviews`);

      const stars = document.createElement('span');
      stars.setAttribute('aria-hidden', 'true');
      stars.textContent = '★★★★★';
      const score = document.createElement('strong');
      score.textContent = ratingValue.toFixed(1);
      const count = document.createElement('small');
      count.textContent = `${reviewCount} reviews`;
      rating.append(stars, score, count);
      title.insertAdjacentElement('afterend', rating);
    };

    const hydrateCardHoverImage = (card) => {
      const template = card.querySelector('template[data-orcl-hover-image]');
      if (!template) return card.querySelector('.orcl-card__image--hover');
      template.replaceWith(template.content.cloneNode(true));
      return card.querySelector('.orcl-card__image--hover');
    };

    const bindCardMedia = (card) => {
      if (card.dataset.orclMediaBound === 'true') return;
      card.dataset.orclMediaBound = 'true';
      const primaryImage = card.querySelector('.orcl-card__image--primary');
      if (!primaryImage) return;

      const hydrate = () => hydrateCardHoverImage(card);
      card.addEventListener('pointerenter', hydrate, { once: true, passive: true });
      card.addEventListener('focusin', hydrate, { once: true });

      primaryImage.addEventListener('error', () => {
        const fallbackImage = hydrate();
        if (!primaryImage.dataset.orclFallbackTried && (fallbackImage?.currentSrc || fallbackImage?.src)) {
          primaryImage.dataset.orclFallbackTried = 'true';
          primaryImage.srcset = '';
          primaryImage.src = fallbackImage.currentSrc || fallbackImage.src;
          primaryImage.classList.add('orcl-card__image--fallback');
          return;
        }
        primaryImage.classList.add('orcl-card__image--unavailable');
      });
    };

    cards.forEach((card) => {
      addLocalReviewPreview(card);
      bindCardMedia(card);
    });

    const waitForCardMedia = async (card) => {
      const images = Array.from(card.querySelectorAll('img'));
      if (images.length === 0) return;

      await Promise.allSettled(
        images.map((image) => {
          if (image.complete) return Promise.resolve();
          if (typeof image.decode === 'function') {
            return image.decode().catch(() => {});
          }

          return new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });
        })
      );
    };

    const fetchCollectionMarkup = async (url) => {
      if (!loadMoreCache.has(url)) {
        loadMoreCache.set(
          url,
          fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          }).then(async (response) => {
            if (!response.ok) throw new Error(`Collection page request failed: ${response.status}`);
            return response.text();
          }).catch((error) => {
            loadMoreCache.delete(url);
            throw error;
          })
        );
      }

      return loadMoreCache.get(url);
    };

    const warmNextPage = (link) => {
      if (!link || link.dataset.prefetched === 'true') return;
      link.dataset.prefetched = 'true';

      const preload = () => {
        fetchCollectionMarkup(link.href).catch(() => {
          link.dataset.prefetched = 'false';
        });
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preload, { timeout: 1200 });
        return;
      }

      window.setTimeout(preload, 180);
    };

    const bindLoadMoreLink = (link) => {
      if (!link || link.dataset.orclBound === 'true') return;
      link.dataset.orclBound = 'true';
      link.addEventListener('click', loadMore);

      if ('IntersectionObserver' in window) {
        if (!loadMoreObserver) {
          loadMoreObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                warmNextPage(entry.target);
                loadMoreObserver?.unobserve(entry.target);
              });
            },
            { rootMargin: '240px 0px' }
          );
        }

        loadMoreObserver.observe(link);
      } else {
        warmNextPage(link);
      }
    };

    const createPaginationState = (label) => {
      const nav = document.createElement('nav');
      nav.className = 'orcl__pagination orcl__pagination--show-more orcl__pagination--end';
      nav.setAttribute('aria-label', 'Collection loading status');
      nav.setAttribute('data-orcl-pagination', '');
      nav.setAttribute('aria-live', 'polite');

      const state = document.createElement('span');
      state.className = 'orcl__show-more orcl__show-more--static';
      state.textContent = label;
      nav.appendChild(state);

      return nav;
    };

    const ensureVisiblePagination = async () => {
      if (!cardGrid || section.querySelector('[data-orcl-pagination]')) return;

      const currentUrl = new URL(window.location.href);
      const hasPagedUrl = currentUrl.searchParams.has('page');
      if (!hasPagedUrl) return;

      try {
        const markup = await fetchCollectionMarkup(currentUrl.toString());
        const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
        const parsedSection = parsedDocument.querySelector('[data-orlaven-collection]');
        const parsedPagination = parsedSection?.querySelector('[data-orcl-pagination]');
        if (!parsedPagination) return;

        parsedPagination.classList.add('is-visible');
        cardGrid.insertAdjacentElement('afterend', parsedPagination);
        const recoveredLink = section.querySelector('[data-orcl-load-more]');
        bindLoadMoreLink(recoveredLink);
        warmNextPage(recoveredLink);
      } catch (error) {
        console.error('[ORLAVEN] Could not recover collection pagination', error);
      }
    };

    const loadMore = async (event) => {
      event.preventDefault();
      const link = event.currentTarget;
      const pagination = link.closest('[data-orcl-pagination]');
      const nextUrl = link.href;
      if (!pagination || link.dataset.loading === 'true') return;

      link.dataset.loading = 'true';
      link.setAttribute('aria-busy', 'true');
      link.classList.add('is-loading');
      pagination.classList.add('is-loading');
      section.classList.add('orcl--loading-more');
      link.dataset.originalLabel = link.textContent;
      link.textContent = 'Loading…';

      try {
        const markup = await fetchCollectionMarkup(nextUrl);
        const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
        const parsedSection = parsedDocument.querySelector('[data-orlaven-collection]');
        const parsedGrid = parsedSection?.querySelector('.orcl__grid');
        if (!parsedGrid || !cardGrid) throw new Error('Collection products could not be found');

        const newCards = Array.from(parsedGrid.children).filter((card) => card.classList.contains('orcl-card'));
        const cardFragment = document.createDocumentFragment();

        newCards.forEach((card, index) => {
          card.removeAttribute('data-orcl-animate');
          card.classList.add('orcl-card--load-more-enter');
          card.style.setProperty('--orcl-load-index', String(Math.min(index, 7)));
          addLocalReviewPreview(card);
          bindCardMedia(card);
          cardFragment.appendChild(card);
        });

        await Promise.race([
          Promise.allSettled(newCards.slice(0, 4).map((card) => waitForCardMedia(card))),
          new Promise((resolve) => {
            window.setTimeout(resolve, 280);
          }),
        ]);
        cardGrid.appendChild(cardFragment);

        const parsedPagination = parsedSection.querySelector('[data-orcl-pagination]');
        pagination.replaceWith(parsedPagination || createPaginationState('All products loaded'));
        const nextPagination = section.querySelector('[data-orcl-pagination]');
        nextPagination?.classList.add('is-visible');
        const nextLink = nextPagination?.querySelector('[data-orcl-load-more]');
        bindLoadMoreLink(nextLink);
        warmNextPage(nextLink);
        if (parsedDocument.title) document.title = parsedDocument.title;
        window.history.replaceState({}, '', nextUrl);
        section.classList.remove('orcl--loading-more');

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            newCards.forEach((card) => {
              card.classList.add('is-visible');

              const finishReveal = () => {
                card.classList.remove('orcl-card--load-more-enter', 'is-visible');
                card.style.removeProperty('--orcl-load-index');
              };

              card.addEventListener('transitionend', finishReveal, { once: true });
              window.setTimeout(finishReveal, 1200);
            });
          });
        });
      } catch (error) {
        console.error('[ORLAVEN] Could not load more products', error);
        section.classList.remove('orcl--loading-more');
        pagination.classList.remove('is-loading');
        link.dataset.loading = 'false';
        link.removeAttribute('aria-busy');
        link.classList.remove('is-loading');
        link.textContent = link.dataset.originalLabel || 'Show more';
      }
    };

    section.querySelectorAll('[data-orcl-load-more]').forEach((link) => {
      bindLoadMoreLink(link);
    });

    ensureVisiblePagination();

    const setFilterButtonState = (isOpen) => {
      filterOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', String(isOpen));
      });
    };

    const closeFilters = () => {
      if (!section.classList.contains('orcl--filters-open')) return;
      const closeId = ++filterCloseId;
      section.classList.add('orcl--filters-closing');
      setFilterButtonState(false);
      let fallbackTimer;
      let finished = false;

      const finishClose = () => {
        if (finished || closeId !== filterCloseId) return;
        finished = true;
        window.clearTimeout(fallbackTimer);
        filterSidebar?.removeEventListener('transitionend', handleTransitionEnd);
        section.classList.remove('orcl--filters-open', 'orcl--filters-closing');
        document.documentElement.classList.remove('orcl-scroll-lock');
        filterSidebar?.setAttribute('aria-hidden', 'true');
        if (filterSidebar) filterSidebar.inert = true;
        lastFilterTrigger?.focus();
      };

      const handleTransitionEnd = (event) => {
        if (event.target === filterSidebar && event.propertyName === 'transform') finishClose();
      };

      if (prefersReducedMotion || !filterSidebar) {
        finishClose();
        return;
      }

      filterSidebar.addEventListener('transitionend', handleTransitionEnd);
      fallbackTimer = window.setTimeout(finishClose, 500);
    };

    const openFilters = (event) => {
      event.preventDefault();
      filterCloseId += 1;
      lastFilterTrigger = event.currentTarget;

      section.classList.remove('orcl--filters-closing');
      section.classList.add('orcl--filters-open');
      document.documentElement.classList.add('orcl-scroll-lock');
      filterSidebar?.setAttribute('aria-hidden', 'false');
      if (filterSidebar) filterSidebar.inert = false;
      setFilterButtonState(true);
      window.requestAnimationFrame(() => filterClose?.focus());
    };

    filterOpenButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', openFilters);
    });

    filterClose?.addEventListener('click', closeFilters);
    filterOverlay?.addEventListener('click', closeFilters);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeFilters();
      if (event.key !== 'Tab' || !section.classList.contains('orcl--filters-open') || !filterSidebar) return;
      const focusable = Array.from(filterSidebar.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (!prefersReducedMotion) {
      filterGroups.forEach((group) => {
        const summary = group.querySelector('summary');
        const values = group.querySelector('.orcl__filter-values');
        if (!summary || !values) return;

        if (group.open) {
          values.style.height = 'auto';
          values.style.opacity = '1';
        }

        const openGroup = () => {
          group.open = true;
          values.style.height = '0px';
          values.style.opacity = '0';
          values.style.transform = 'translate3d(0, -6px, 0)';

          window.requestAnimationFrame(() => {
            values.style.height = `${values.scrollHeight}px`;
            values.style.opacity = '1';
            values.style.transform = 'translate3d(0, 0, 0)';
          });

          window.setTimeout(() => {
            if (group.open) values.style.height = 'auto';
          }, 380);
        };

        const closeGroup = () => {
          values.style.height = `${values.scrollHeight}px`;
          values.style.opacity = '1';
          values.style.transform = 'translate3d(0, 0, 0)';

          window.requestAnimationFrame(() => {
            values.style.height = '0px';
            values.style.opacity = '0';
            values.style.transform = 'translate3d(0, -6px, 0)';
          });

          window.setTimeout(() => {
            group.open = false;
            values.style.height = '';
            values.style.opacity = '';
            values.style.transform = '';
          }, 340);
        };

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          if (group.open) closeGroup();
          else openGroup();
        });
      });
    }

    const sortComparator = (sortValue) => {
      switch (sortValue) {
        case 'price-ascending':
          return (left, right) => Number(left.dataset.orclPrice || 0) - Number(right.dataset.orclPrice || 0);
        case 'price-descending':
          return (left, right) => Number(right.dataset.orclPrice || 0) - Number(left.dataset.orclPrice || 0);
        case 'title-ascending':
          return (left, right) => String(left.dataset.orclTitle || '').localeCompare(String(right.dataset.orclTitle || ''));
        case 'title-descending':
          return (left, right) => String(right.dataset.orclTitle || '').localeCompare(String(left.dataset.orclTitle || ''));
        default:
          return (left, right) =>
            Number(left.dataset.orclDefaultOrder || 0) - Number(right.dataset.orclDefaultOrder || 0);
      }
    };

    const applySequenceSort = () => {
      if (!isSequenceCollection || !cardGrid) return;
      const params = new URLSearchParams(window.location.search);
      const sortedCards = [...cards].sort(sortComparator(sortSelect?.value || params.get('sort_by') || ''));
      sortedCards.forEach((card) => cardGrid.appendChild(card));
    };

    const applySequencePriceFilter = () => {
      if (!isSequenceTagFallback) return;
      const params = new URLSearchParams(window.location.search);
      const minPrice = Number(params.get('filter.v.price.gte') || 0);
      const maxPrice = Number(params.get('filter.v.price.lte') || 0);
      const hasPriceFilter = Boolean(minPrice) || Boolean(maxPrice);

      let visibleCount = 0;
      cards.forEach((card) => {
        const price = Number(card.dataset.orclPrice || 0);
        const matches = (!minPrice || price >= minPrice) && (!maxPrice || price <= maxPrice);
        card.hidden = hasPriceFilter ? !matches : false;
        if (!hasPriceFilter || matches) visibleCount += 1;
      });

      if (countNode) {
        countNode.textContent = hasPriceFilter
          ? `${visibleCount} ${visibleCount === 1 ? 'piece' : 'pieces'}`
          : defaultCountText;
      }
    };

    filterForm?.addEventListener('submit', (event) => {
      if (!isSequenceTagFallback) return;

      const minInput = filterForm.querySelector('input[name="filter.v.price.gte"]');
      const maxInput = filterForm.querySelector('input[name="filter.v.price.lte"]');
      const hasSizeFilter = filterForm.querySelector('input[name="filter.v.option.size"]:checked');
      const nextUrl = new URL(window.location.href);
      const minValue = minInput?.value.trim() || '';
      const maxValue = maxInput?.value.trim() || '';

      if ((!minValue && !maxValue) || hasSizeFilter) return;

      event.preventDefault();

      if (minValue) nextUrl.searchParams.set('filter.v.price.gte', minValue);
      else nextUrl.searchParams.delete('filter.v.price.gte');

      if (maxValue) nextUrl.searchParams.set('filter.v.price.lte', maxValue);
      else nextUrl.searchParams.delete('filter.v.price.lte');

      window.history.replaceState({}, '', nextUrl.toString());
      applySequencePriceFilter();
      closeFilters();
    });

    sortSelect?.addEventListener('change', () => {
      sortForm?.submit();
    });

    const revealItems = section.querySelectorAll('[data-orcl-animate]');
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );

      revealItems.forEach((item) => observer.observe(item));
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    applySequenceSort();
    applySequencePriceFilter();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOrlavenCollections);
} else {
  initOrlavenCollections();
}
